var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { faCog, faDatabase, faEdit, faPlus, faTrash, faTrashRestore, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useEventBus } from "@vueuse/core";
import axios from "axios";
import { GalaxyApi } from "@/api";
import Filtering, { contains, equals, toBool } from "@/utils/filtering";
import _l from "@/utils/localization";
import { withPrefix } from "@/utils/redirect";
import { errorMessageAsString } from "@/utils/simple-error";
import {} from "./types";
const { emit } = useEventBus("grid-router-push");
/**
 * Request and return data from server
 */
function getData(offset, limit, search, sort_by, sort_desc) {
    return __awaiter(this, void 0, void 0, function* () {
        const query = {
            limit: String(limit),
            offset: String(offset),
            search: search,
            sort_by: sort_by,
            sort_desc: String(sort_desc),
        };
        const queryString = new URLSearchParams(query).toString();
        const { data } = yield axios.get(withPrefix(`/admin/quotas_list?${queryString}`));
        return [data.rows, data.rows_total];
    });
}
/**
 * Actions are grid-wide operations
 */
const actions = [
    {
        title: "Create New Quota",
        icon: faPlus,
        handler: () => {
            emit("/admin/form/create_quota");
        },
    },
];
/**
 * Declare columns to be displayed
 */
const fields = [
    {
        key: "name",
        title: "Name",
        type: "operations",
        operations: [
            {
                title: "Edit Name/Description",
                icon: faEdit,
                condition: (data) => !data.deleted,
                handler: (data) => {
                    emit(`/admin/form/rename_quota?id=${data.id}`);
                },
            },
            {
                title: "Manage Users and Groups",
                icon: faUsers,
                condition: (data) => !data.deleted && !data.default_type,
                handler: (data) => {
                    emit(`/admin/form/manage_users_and_groups_for_quota?id=${data.id}`);
                },
            },
            {
                title: "Change Amount",
                icon: faDatabase,
                condition: (data) => !data.deleted,
                handler: (data) => {
                    emit(`/admin/form/edit_quota?id=${data.id}`);
                },
            },
            {
                title: "Change Default",
                icon: faCog,
                condition: (data) => !data.deleted,
                handler: (data) => {
                    emit(`/admin/form/set_quota_default?id=${data.id}`);
                },
            },
            {
                title: "Delete",
                icon: faTrash,
                condition: (data) => !data.deleted,
                handler: (data) => __awaiter(void 0, void 0, void 0, function* () {
                    if (confirm(_l("Are you sure that you want to delete this quota?"))) {
                        const { error } = yield GalaxyApi().DELETE("/api/quotas/{id}", {
                            params: {
                                path: { id: String(data.id) },
                            },
                        });
                        if (error) {
                            return {
                                status: "danger",
                                message: `Failed to delete '${data.name}': ${errorMessageAsString(error)}`,
                            };
                        }
                        return {
                            status: "success",
                            message: `'${data.name}' has been deleted.`,
                        };
                    }
                }),
            },
            {
                title: "Purge",
                icon: faTrash,
                condition: (data) => !!data.deleted,
                handler: (data) => __awaiter(void 0, void 0, void 0, function* () {
                    if (confirm(_l("Are you sure that you want to purge this quota?"))) {
                        const { error } = yield GalaxyApi().POST("/api/quotas/{id}/purge", {
                            params: {
                                path: { id: String(data.id) },
                            },
                        });
                        if (error) {
                            return {
                                status: "danger",
                                message: `Failed to purge '${data.name}': ${errorMessageAsString(error)}`,
                            };
                        }
                        return {
                            status: "success",
                            message: `'${data.name}' has been purged.`,
                        };
                    }
                }),
            },
            {
                title: "Restore",
                icon: faTrashRestore,
                condition: (data) => !!data.deleted,
                handler: (data) => __awaiter(void 0, void 0, void 0, function* () {
                    const { error } = yield GalaxyApi().POST("/api/quotas/deleted/{id}/undelete", {
                        params: {
                            path: { id: String(data.id) },
                        },
                    });
                    if (error) {
                        return {
                            status: "danger",
                            message: `Failed to restore '${data.name}': ${errorMessageAsString(error)}`,
                        };
                    }
                    return {
                        status: "success",
                        message: `'${data.name}' has been restored.`,
                    };
                }),
            },
        ],
    },
    {
        key: "description",
        title: "Description",
        type: "text",
    },
    {
        key: "amount",
        title: "Amount",
        type: "text",
    },
    {
        key: "groups",
        title: "Groups",
        type: "text",
    },
    {
        key: "users",
        title: "Users",
        type: "text",
    },
    {
        key: "default_type",
        title: "Default",
        type: "text",
    },
    {
        key: "quota_source_label",
        title: "Source",
        type: "text",
    },
    {
        key: "update_time",
        title: "Updated",
        type: "date",
    },
];
const validFilters = {
    name: { placeholder: "name", type: String, handler: contains("name"), menuItem: true },
    description: { placeholder: "description", type: String, handler: contains("description"), menuItem: true },
    deleted: {
        placeholder: "Deleted",
        type: Boolean,
        boolType: "is",
        handler: equals("deleted", "deleted", toBool),
        menuItem: true,
    },
};
/**
 * Grid configuration
 */
const gridConfig = {
    id: "quotas-grid",
    actions: actions,
    fields: fields,
    filtering: new Filtering(validFilters, undefined, false, false),
    getData: getData,
    plural: "Quotas",
    sortBy: "name",
    sortDesc: true,
    sortKeys: ["name", "description", "update_time"],
    title: "Quotas",
};
export default gridConfig;
